// eslint-disable-next-line simple-import-sort/imports
import type { ComponentProps, ReactNode } from 'react';
import { type Ref, useCallback, useEffect, useMemo, useRef } from 'react';

import { merge } from 'lodash-es';
import type {
  ActionMeta,
  CSSObjectWithLabel,
  GroupBase,
  MultiValueProps,
  OnChangeValue,
  OptionProps,
  Props as ReactSelectProps,
  SelectInstance,
  StylesConfig,
  ValueContainerProps,
} from 'react-select';
import ReactSelect, { components as ReactSelectComponents } from 'react-select';
import { useTheme } from 'styled-components';
import type { ColorMapping } from 'styles/config/colors';
import sizes from 'styles/config/sizes';
import text from 'styles/config/text';

import { CheckIcon, TriangleArrowDownIcon } from 'components/atoms/Icons';
import InputWrapper from 'components/atoms/InputWrapper';
import Text from 'components/atoms/Text';
import * as Styled from './Select.styled';
import type { SelectAllOptionType } from './types';

const getDefaultStyles = ({
  colors,
  inverse,
  disabled,
}: {
  colors: ColorMapping;
  inverse?: boolean;
  disabled?: boolean;
}): { [key: string]: CSSObjectWithLabel } => {
  const defaultStyles = {
    control: {
      padding: '5px 10px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: inverse ? colors.borderSubtleInverse : colors.borderStrong,
      borderRadius: '8px',
      outline: 'none',
      boxShadow: 'none',
      ':focus': {
        borderColor: inverse ? colors.backgroundInverse : colors.borderPrimary,
      },
      background: inverse ? colors.backgroundInverse : colors.background,
    },
    menuList: {
      border: `1px solid ${inverse ? colors.borderSubtleInverse : colors.borderPrimary}`,
      margin: 0,
      gap: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
    menu: {
      margin: 0,
      boxShadow: 'none',
      zIndex: 2,
      background: inverse ? colors.backgroundInverse : colors.background,
    },
    option: {
      borderRadius: 0,
      padding: '15px 20px',
      color: inverse ? colors.textPrimaryInverse : colors.textPrimary,
    },
    dropdownIndicator: {
      transition: 'color 150ms, rotate 150ms',
    },
    indicatorSeparator: {
      display: 'none',
    },
    placeholder: {
      '& h3': {
        fontSize: '14px',
      },
    },
    input: {
      fontSize: text.size.body.regular,
    },
    singleValue: {
      color: inverse ? colors.textPrimaryInverse : colors.textPrimary,
    },
  };

  const disabledStyles = {
    control: {
      background: inverse
        ? colors.backgroundInverseHover
        : colors.backgroundPage,
    },
  };

  if (disabled) {
    return merge(defaultStyles, disabledStyles) as {
      [key: string]: CSSObjectWithLabel;
    };
  }

  return defaultStyles;
};

export type OptionType = {
  label: string;
  value: string;
  hint?: string;
  isDisabled?: boolean;
};

export type SelectProps<
  OT extends OptionType = OptionType,
  isMulti extends boolean = boolean,
> = ReactSelectProps<OT, isMulti, GroupBase<OT>> & {
  selectRef?: Ref<SelectInstance<OT, isMulti>>;
  errors?: string[];
  errorIcon?: ReactNode;
  label?: string;
  help?: ReactNode;
  helpPlacement?: ComponentProps<typeof InputWrapper>['helpPlacement'];
  readonly?: boolean;
  'data-test-id'?: string;
  'data-dd-action'?: string;
  fullWidth?: boolean;
  portal?: boolean;
  inverse?: boolean;
  withOptionSelectedIndicator?: boolean;
  selectAllOption?: SelectAllOptionType;
};

const CustomLoadingIndicator = () => {
  return <Styled.Loader spinnerText="" spinner={true} />;
};

// Prevents auto-scroll to bottom when clicking multi-select
const ValueContainer = <
  OT extends OptionType,
  IsMulti extends boolean = boolean,
>(
  props: ValueContainerProps<OT, IsMulti>,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const scrollPos = useRef(0);
  const shouldRestore = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const onScroll = () => {
      if (shouldRestore.current) {
        el.scrollTop = scrollPos.current;
        shouldRestore.current = false;
      }
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (ref.current) {
      scrollPos.current = ref.current.scrollTop;
      shouldRestore.current = true;
      setTimeout(() => {
        shouldRestore.current = false;
      }, 100);
    }
    props.innerProps?.onMouseDown?.(e);
  };

  return (
    <ReactSelectComponents.ValueContainer
      {...props}
      innerProps={{ ...props.innerProps, ref, onMouseDown }}
    />
  );
};

const Select = <OT extends OptionType, isMulti extends boolean>({
  components,
  styles,
  menuPlacement,
  placeholder,
  formatOptionLabel,
  selectRef,
  errors = [],
  errorIcon,
  label,
  help,
  value,
  isLoading,
  loadingMessage,
  readonly = false,
  'data-test-id': dataTestId,
  'data-dd-action': ddActionName,
  fullWidth,
  portal,
  inverse,
  withOptionSelectedIndicator = false,
  isSearchable = false,
  selectAllOption,
  helpPlacement,
  isMulti,
  isDisabled,
  onChange,
  options,
  ...restProps
}: SelectProps<OT, isMulti>): React.JSX.Element => {
  const {
    activeColorScheme: { colors },
  } = useTheme();

  const optionsWithSelectAll = useMemo(() => {
    const opts = (options as OT[] | undefined) ?? [];

    if (!selectAllOption || !isMulti) {
      return opts;
    }

    const selectAll = { ...(selectAllOption as OT) };
    const result = [
      selectAll,
      ...opts.filter(o => o.value !== (selectAll as OT).value),
    ];

    return result;
  }, [selectAllOption, isMulti, options]);

  const defaultStyles = getDefaultStyles({
    colors,
    inverse,
    disabled: isDisabled,
  });

  // NB: the styling will fail if menuPlacement="auto" and the menu flips
  // to top. At the moment I can't see a way of finding the placement of
  // the menu for custom styling
  const customStyle: StylesConfig<OT, isMulti> = {
    ...styles,
    dropdownIndicator: base => ({
      ...base,
      ...defaultStyles.dropdownIndicator,
    }),
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    control: (base, props) => {
      let borderColor = inverse
        ? colors.borderSubtleInverse
        : props.menuIsOpen
          ? colors.borderPrimary
          : colors.borderStrong;
      if (errors.length) {
        borderColor = colors.borderDanger;
      }
      if (props.isFocused && !errors.length) {
        borderColor = inverse
          ? colors.borderSubtleInverse
          : colors.borderPrimary;
      }
      const border: CSSObjectWithLabel = {};
      const hover: CSSObjectWithLabel = { borderColor };
      if (props.menuIsOpen) {
        if (props.selectProps.menuPlacement === 'bottom') {
          // Keep red border when there are errors, otherwise use background color for seamless connection
          border.borderBottomColor = hover.borderBottomColor = errors.length
            ? colors.borderDanger
            : inverse
              ? colors.borderSubtleInverse
              : colors.background;
          border.borderRadius = '8px 8px 0 0';
        }
        if (props.selectProps.menuPlacement === 'top') {
          // Keep red border when there are errors, otherwise use background color for seamless connection
          border.borderTopColor = hover.borderTopColor = errors.length
            ? colors.borderDanger
            : inverse
              ? colors.borderSubtleInverse
              : colors.background;
          border.borderRadius = '0 0 8px 8px';
        }
      }

      return {
        ...base,
        ...defaultStyles.control,
        ...border,
        borderColor,
        '&:hover': hover,
        '& .triangleArrowDownIcon svg': {
          transform: props.menuIsOpen ? 'rotate(180deg)' : 'none',
        },
        '[data-check-icon]': {
          display: 'none',
        },
        cursor: 'pointer',
        minHeight: sizes.inputHeight,
        overflow: 'hidden',
        ...(styles?.control ? styles.control(base, props) : {}),
      } as CSSObjectWithLabel;
    },
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    option: (base, props) => {
      const border: CSSObjectWithLabel = {};
      const borderColor = inverse
        ? colors.borderSubtleInverse
        : colors.borderStrong;
      if (props.selectProps.menuPlacement === 'bottom') {
        border.borderTop = `1px solid ${borderColor}`;
      }
      if (props.selectProps.menuPlacement === 'top') {
        border.borderBottom = `1px solid ${borderColor}`;
      }

      let backgroundColor;
      if (props.data.isDisabled) {
        backgroundColor = inverse
          ? colors.backgroundInverseHover
          : colors.backgroundHover;
      } else {
        backgroundColor = inverse
          ? props.isFocused
            ? colors.backgroundInverseHover
            : colors.backgroundInverse
          : props.isFocused
            ? colors.backgroundPage
            : colors.background;
      }

      return {
        ...base,
        ...defaultStyles.option,
        backgroundColor,
        cursor: 'pointer',
        ...border,
        ...(styles?.option ? styles.option(base, props) : {}),
        '&:first-of-type':
          props.selectProps.menuPlacement === 'top'
            ? {
                borderBottom: 'none',
              }
            : {
                borderTop: 'none',
              },
      } as CSSObjectWithLabel;
    },
    menu: (base, props) => {
      return {
        ...base,
        ...defaultStyles.menu,
        ...(styles?.menu ? styles.menu(base, props) : {}),
      };
    },
    menuList: (base, props) => {
      const border: CSSObjectWithLabel = {};
      if (errors.length) {
        border.borderColor = colors.borderDanger;
      }
      if (props.selectProps.menuPlacement === 'bottom') {
        border.borderTopColor = inverse
          ? colors.borderSubtleInverse
          : colors.borderStrong;
      }
      if (props.selectProps.menuPlacement === 'top') {
        border.borderBottomColor = inverse
          ? colors.borderSubtleInverse
          : colors.borderStrong;
      }

      return {
        ...base,
        ...defaultStyles.menuList,
        '&:first-of-type': {
          borderRadius:
            props.selectProps.menuPlacement === 'top'
              ? '8px 8px 0 0'
              : '0 0 8px 8px',
        },
        ...border,
        ...(styles?.menuList ? styles.menuList(base, props) : {}),
      } as CSSObjectWithLabel;
    },
    indicatorSeparator: (base, props) => {
      return {
        ...base,
        ...defaultStyles.indicatorSeparator,
        ...(styles?.indicatorSeparator
          ? styles.indicatorSeparator(base, props)
          : {}),
      };
    },
    placeholder: (base, props) => ({
      ...base,
      ...defaultStyles.placeholder,
      ...(styles?.placeholder ? styles.placeholder(base, props) : {}),
    }),
    input: (base, props) => ({
      ...base,
      ...defaultStyles.input,
      ...(styles?.input ? styles.input(base, props) : {}),
    }),
    valueContainer: (base, props) => ({
      ...base,
      maxHeight: `calc(${sizes.inputHeight} * 3.5)`,
      overflowY: 'auto',
      flexWrap: 'wrap',
      padding: '2px 0px',
      ...(styles?.valueContainer ? styles.valueContainer(base, props) : {}),
    }),
    multiValue: (base, props) => ({
      ...base,
      borderRadius: '8px',
      backgroundColor: colors.backgroundAccent,
      ...(styles?.multiValue ? styles.multiValue(base, props) : {}),
    }),
    multiValueLabel: (base, props) => ({
      ...base,
      color: colors.textPrimary,
      fontSize: text.size.body.small,
      ...(styles?.multiValueLabel ? styles.multiValueLabel(base, props) : {}),
    }),
    multiValueRemove: (base, props) => ({
      ...base,
      color: colors.textSecondary,
      ':hover': {
        backgroundColor: colors.backgroundHover,
        color: colors.textPrimary,
      },
      ...(styles?.multiValueRemove ? styles.multiValueRemove(base, props) : {}),
    }),
    singleValue: (base, props) => ({
      ...base,
      ...defaultStyles.singleValue,
      ...(styles?.singleValue ? styles.singleValue(base, props) : {}),
    }),
  };

  const handleChange = useCallback(
    (newValue: OnChangeValue<OT, isMulti>, actionMeta: ActionMeta<OT>) => {
      if (!isMulti || !selectAllOption) {
        onChange?.(newValue, actionMeta);
        return;
      }

      const next = Array.isArray(newValue) ? (newValue as OT[]) : [];
      const clicked = actionMeta?.option as OT | undefined;
      const removedValue = actionMeta?.removedValue as OT | undefined;
      const { value: allValue } = selectAllOption;
      const allOption = selectAllOption as OT;

      // Get all regular options (excluding the "All Agents" option)
      const regularOptions =
        (options as OT[] | undefined)?.filter(opt => opt.value !== allValue) ||
        [];
      const currentValue = Array.isArray(value) ? (value as OT[]) : [];

      // Handle removal of the "All" chip directly
      if (
        actionMeta.action === 'remove-value' &&
        removedValue?.value === allValue
      ) {
        // Deselect all options
        onChange?.([] as unknown as OnChangeValue<OT, isMulti>, actionMeta);
        return;
      }

      // Handle clicking the "All" value checkbox in the dropdown
      if (clicked?.value === allValue) {
        const currentRegularValues = currentValue.filter(
          v => v.value !== allValue,
        );

        if (currentRegularValues.length === regularOptions.length) {
          // All regular options are selected, so deselect all
          onChange?.([] as unknown as OnChangeValue<OT, isMulti>, actionMeta);
        } else {
          // Not all regular options are selected, so select all regular options + "All" chip
          onChange?.(
            [allOption, ...regularOptions] as unknown as OnChangeValue<
              OT,
              isMulti
            >,
            actionMeta,
          );
        }
        return;
      }

      // Handle regular option clicks
      const withoutAll = next.filter(v => v.value !== allValue);

      // Check if all regular options are now selected
      if (withoutAll.length === regularOptions.length) {
        // All regular options are selected, add the "All" chip
        onChange?.(
          [allOption, ...withoutAll] as unknown as OnChangeValue<OT, isMulti>,
          actionMeta,
        );
      } else {
        // Not all regular options are selected, don't include the "All" chip
        onChange?.(
          withoutAll as unknown as OnChangeValue<OT, isMulti>,
          actionMeta,
        );
      }
    },
    [isMulti, selectAllOption, onChange, options, value],
  );

  const DefaultDropdownIndicator = useCallback(
    (props: any) => {
      return (
        <Styled.DropdownIndicator {...props}>
          <TriangleArrowDownIcon
            aria-hidden={readonly ? true : false}
            opacity={readonly ? 0 : 1}
          />
        </Styled.DropdownIndicator>
      );
    },
    [readonly],
  );

  const CustomOption = useCallback(
    (optionProps: OptionProps<OT, isMulti>) => {
      // Pass selectAllOption through the selectProps to custom components
      const enhancedProps = {
        ...optionProps,
        selectProps: {
          ...(optionProps.selectProps || {}),
          selectAllOption,
          options: optionsWithSelectAll, // Also pass the full options array
        },
      };

      // Use the custom Option component if provided, otherwise use the default
      if (components && components.Option) {
        const OptionComponent = components.Option;
        return <OptionComponent {...enhancedProps} />;
      }

      // Fallback to default Option component
      return <ReactSelectComponents.Option {...enhancedProps} />;
    },
    [components, selectAllOption, optionsWithSelectAll],
  );

  const CustomMultiValue = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    (multiValueProps: MultiValueProps<OT, isMulti>) => {
      // Check if selectAll is configured and we're in multi-select mode
      if (selectAllOption && isMulti) {
        const allOptions = (options as OT[] | undefined) || [];
        const regularOptions = allOptions.filter(
          opt => opt.value !== selectAllOption.value,
        );
        const selectedValues = Array.isArray(value) ? (value as OT[]) : [];
        const selectedRegularOptions = selectedValues.filter(
          v => v.value !== selectAllOption.value,
        );

        const allSelected =
          selectedRegularOptions.length === regularOptions.length &&
          regularOptions.length > 0;

        if (allSelected) {
          // Only show the selectAll chip
          if (multiValueProps.data.value !== selectAllOption.value) {
            return null; // Hide individual chips
          }
        } else {
          // Don't show the selectAll chip (if somehow present)
          if (multiValueProps.data.value === selectAllOption.value) {
            return null;
          }
        }
      }

      // Use the custom MultiValue component if provided, otherwise use the default
      if (components && components.MultiValue) {
        const MultiValueComponent = components.MultiValue;
        return <MultiValueComponent {...multiValueProps} />;
      }

      // Fallback to default MultiValue component
      return <ReactSelectComponents.MultiValue {...multiValueProps} />;
    },
    [components, selectAllOption, isMulti, options, value],
  );

  return (
    <InputWrapper
      data-test-id={dataTestId}
      errorIcon={errorIcon}
      errors={errors}
      fullWidth={fullWidth}
      help={help}
      inverse={inverse}
      label={label}
      portal={portal}
      labelAsDiv={true}
      helpPlacement={helpPlacement}
    >
      <Styled.SelectWrapper
        $fullWidth={fullWidth}
        data-dd-action={ddActionName}
      >
        <ReactSelect
          ref={selectRef}
          {...restProps}
          components={{
            DropdownIndicator: DefaultDropdownIndicator,
            LoadingIndicator: CustomLoadingIndicator,
            ValueContainer,
            ...(components || {}),
            Option: CustomOption,
            MultiValue: CustomMultiValue,
          }}
          formatOptionLabel={
            formatOptionLabel ||
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
            ((option: OptionType) => {
              const textNode = (
                <Text
                  as="span"
                  className="flex items-center"
                  color={
                    option.isDisabled
                      ? inverse
                        ? 'textHelperInverse'
                        : 'textHelper'
                      : inverse
                        ? isDisabled
                          ? 'textDisabledInverse'
                          : 'textPrimaryInverse'
                        : isDisabled
                          ? 'textDisabled'
                          : 'textPrimary'
                  }
                >
                  {option.label}
                </Text>
              );
              return withOptionSelectedIndicator ? (
                <div className="flex justify-between items-center">
                  {textNode}
                  {value &&
                  ((Array.isArray(value) &&
                    value.some(
                      v => 'value' in v && v.value === option.value,
                    )) ||
                    (!Array.isArray(value) &&
                      'value' in value &&
                      value.value === option.value)) ? (
                    <CheckIcon color="textAccent" data-check-icon={true} />
                  ) : null}
                </div>
              ) : (
                textNode
              );
            })
          }
          isDisabled={isDisabled}
          isLoading={isLoading}
          isMulti={isMulti}
          isSearchable={isSearchable}
          loadingMessage={loadingMessage}
          menuIsOpen={readonly ? false : undefined}
          menuPlacement={menuPlacement}
          menuPortalTarget={
            portal && typeof document !== 'undefined'
              ? document.body
              : undefined
          }
          options={optionsWithSelectAll}
          placeholder={<Text as="span">{placeholder || 'Select...'}</Text>}
          styles={customStyle}
          value={value}
          onChange={handleChange}
        />
      </Styled.SelectWrapper>
    </InputWrapper>
  );
};

export default Select;
